import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonSidebar from '../../Components/CommonSidebar';
import CommonHeader from '../../Components/CommonHeader';
import CommonButton from '../../Components/CommonButton';
import ModuleIcon from '../../Components/ModuleIcon';
import CommonLoader from '../../Components/CommonLoader';
import { useConfirm } from '../../Components/ConfirmModal';
import { useToast } from '../../Components/CommonToast';
import './RiskPortal1.css';
import AddRisk from './Forms/AddRisk';
import RiskDetail from './Pages/RiskDetail';
import { RbacProvider, useRbac, ROLES, humanizeRole } from './rbac';
import { buildSidebarGroups, getView, DEFAULT_VIEW } from './portalViews';
import { useRiskDashboard } from './useRiskDashboard';
import { delete_risk, get_by_id_risk } from '../../services';
import { Body } from '../../Constants/Fonts';

// Unwrap the API response envelope (same shape helper used across the portal).
const extractItem = (res) =>
    res?.data?.response_data?.data || res?.data?.data ||
    res?.statusCode?.response_data?.data || res?.statusCode?.data || null;

// ═══════════════════════════════════════════════════════════════
// Risk Portal shell — the module entry point.
// ---------------------------------------------------------------
// A thin container: it owns the shared state (active view, scope filters, the
// create/edit-risk modal, the risk-detail drill-in) and the cross-screen
// actions, then delegates ALL screen rendering + navigation to the declarative
// view registry (portalViews.js). Role-based visibility is enforced by rbac.js
// (canView) — the shell never hard-codes which role sees what.
// ═══════════════════════════════════════════════════════════════
function RiskPortalShell() {
    const navigate = useNavigate();
    const { config, role, can, canView } = useRbac();
    const [active, setActive] = useState(DEFAULT_VIEW);
    const [businessUnit, setBusinessUnit] = useState('all');
    const [project, setProject] = useState('all');
    const [showAddRisk, setShowAddRisk] = useState(false);
    const [editingRisk, setEditingRisk] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [detailRiskId, setDetailRiskId] = useState(null);
    const [creatingRisk, setCreatingRisk] = useState(false);

    const { notify, toastNode } = useToast();
    const { confirm, confirmModal } = useConfirm();
    const dash = useRiskDashboard({ buId: businessUnit, projectId: project });

    // Role-specific home dashboards: Risk Owner → "My Risk Tasks", Risk Manager
    // → "Risk Manager Dashboard"; every other role → the org-wide analytics home.
    const isOwnerHome = role === ROLES.OWNER;
    const isManagerHome = role === ROLES.MANAGER;

    // The active view descriptor (falls back to the default landing view).
    const view = getView(active) || getView(DEFAULT_VIEW);

    // Keep `active` on a view this role may actually see. If the resolved role
    // can't open the current view (initial load, or role changed), snap to its
    // first allowed view. Hiding the nav item is UX; this is what stops a stale
    // or forced state from landing on a view the role has no access to.
    useEffect(() => {
        if (!canView(active)) setActive(config.views[0] || DEFAULT_VIEW);
    }, [canView, active, config]);

    // Navigate only to a permitted view (safe no-op otherwise).
    const goTo = (id) => { if (canView(id)) setActive(id); };

    const handleDeleteRisk = async (row) => {
        const ok = await confirm({
            title: 'Delete risk',
            message: `Delete ${row.risk_id || 'this risk'}? This can't be undone.`,
            confirmLabel: 'Delete', danger: true, themeColor: false,
        });
        if (!ok) return;
        try {
            await delete_risk({ id: row.id });
            notify(`Risk ${row.risk_id || ''} deleted`.trim(), 'success');
            dash.reload();
        } catch (e) {
            console.error('Delete risk failed', e);
            notify('Failed to delete risk', 'error');
        }
    };

    // Edit a risk: fetch its full record, then open AddRisk pre-populated.
    const handleEditRisk = async (row) => {
        setEditLoading(true);
        try {
            const raw = extractItem(await get_by_id_risk({ id: row.id }));
            setEditLoading(false);
            if (!raw) return;
            setEditingRisk(raw);
            setShowAddRisk(true);
        } catch (e) {
            setEditLoading(false);
            console.error('Load risk for edit failed', e);
        }
    };

    // "+ New Risk" opens the risk workflow on its first stage (the Identify form).
    const openCreateRisk = () => { setDetailRiskId(null); setCreatingRisk(true); };
    const closeRiskForm = () => { setShowAddRisk(false); setEditingRisk(null); };

    // The context every registry render/actions callback receives. Everything a
    // screen needs — data, permission checks, and cross-screen actions — flows
    // through here, so screens stay decoupled from the shell's internals. Built
    // fresh each render (a cheap object; screens re-render regardless), which
    // keeps the cross-screen callbacks free of stale closures.
    const ctx = {
        dash,
        role, can, canView,
        isOwnerHome,
        isManagerHome,
        scope: { businessUnit, project, setBusinessUnit, setProject },
        goTo,
        openRiskDetail: (id) => { setCreatingRisk(false); setDetailRiskId(id); },
        openCreateRisk,
        editRisk: handleEditRisk,
        deleteRisk: handleDeleteRisk,
        Button: CommonButton,
    };

    // Sidebar groups for this role (empty groups dropped), with ModuleIcon glyphs.
    const sidebarGroups = useMemo(
        () => buildSidebarGroups(canView, (name) => <ModuleIcon name={name} size={18} />),
        [canView],
    );

    // Header action buttons for the active view (hidden while drilled into a risk).
    const headerActions = detailRiskId == null && !creatingRisk && view.actions ? view.actions(ctx) : null;

    return (
        <div className="rp1_root  theme-light">
            <CommonSidebar
                groups={sidebarGroups}
                activeId={active}
                onSelect={goTo}
                subtitle="Risk Portal"
                ariaLabel="Risk Portal navigation"
                style={{ gridArea: 'sidebar', height: '100%', minHeight: 0, overflowY: 'auto' }}
            />

            <CommonHeader
                className="rp1_header"
                userRole={config?.label || humanizeRole(role)}
                org={<Body as="span" className="rp1_tenant_chip">Org: <strong>Foresee Security</strong></Body>}
                backToDashboard={<CommonButton text="← Back to Dashboard" onClick={() => navigate('/product-dashboard')} />}
            />

            <main className=" rp1_pane">
                {headerActions && (
                    <div className="rp1_page_header">
                        <div className="rp1_page_actions">{headerActions}</div>
                    </div>
                )}

                {detailRiskId != null || creatingRisk ? (
                    <RiskDetail
                        riskId={creatingRisk ? null : detailRiskId}
                        role={role}
                        categories={dash.categories}
                        businessUnits={dash.businessUnits}
                        projects={dash.projects}
                        onBack={() => { setCreatingRisk(false); setDetailRiskId(null); dash.reload(); }}
                    />
                ) : view.render && !view.comingSoon ? (
                    view.render(ctx)
                ) : (
                    // Declared-but-not-yet-built view → a single white body card.
                    <div className=" rp1_empty">
                        <div className="rp1_empty_icon"><ModuleIcon name={view.icon} size={26} /></div>
                        <div className="rp1_empty_title">{view.title}</div>
                        <p className="rp1_empty_hint">{view.subtitle} — content coming soon.</p>
                    </div>
                )}
            </main>

            <AddRisk
                show={showAddRisk}
                onHide={closeRiskForm}
                editingRisk={editingRisk}
                categories={dash.categories}
                businessUnits={dash.businessUnits}
                projects={dash.projects}
                role={role}
                onSaved={(data) => {
                    const wasEdit = !!editingRisk;
                    const rid = data?.risk_id || editingRisk?.risk_id || '';
                    closeRiskForm();
                    dash.reload();
                    notify(`Risk ${rid} ${wasEdit ? 'updated' : 'created'}`.replace('  ', ' ').trim(), 'success');
                    // After creating a new risk, drill straight into its detail page
                    // so the user lands on the workflow for the risk they just made.
                    if (!wasEdit && data?.id != null) setDetailRiskId(data.id);
                }}
                onError={(msg) => notify(msg || 'Failed to save risk', 'error')}
            />

            {editLoading && <CommonLoader fullPage label="Loading risk…" />}
            {confirmModal}
            {toastNode}
        </div>
    );
}

// Wrap the portal in the RBAC provider so every view (and future Forms/Tables)
// can read the current role + permissions via useRbac(). The provider loads the
// role registry from the backend (falling back to the built-in defaults), so
// admin-created roles flow through automatically.
export default function RiskPortal() {
    return (
        <RbacProvider>
            <RiskPortalShell />
        </RbacProvider>
    );
}
